import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Rocket } from 'lucide-react';
import { heroData } from '../../data/mock';
import NetworkParticles from './NetworkParticles';

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-red-50 pt-20">
      {/* Network Particles Background */}
      <div className="absolute inset-0">
        <NetworkParticles />
      </div>

      {/* Soft gradient blobs (behind particles) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            x: [0, 30, 0],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-red-100/40 blur-[80px]"
        />
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            y: [0, 20, 0],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          className="absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-orange-100/40 blur-[60px]"
        />
      </div>

      {/* Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle, #000 1px, transparent 1px)`,
          backgroundSize: '30px 30px'
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-100 border border-red-200 mb-8"
        >
          <Rocket className="w-4 h-4 text-red-600" />
          <span className="text-red-700 text-sm font-medium">Direktorat Pusat Data & Digitalisasi</span>
        </motion.div>

        {/* Headline with animated border */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="relative inline-block mb-6"
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
            <span className="text-gray-900">Akselerasi Digital</span>
            <br />
            <span 
              className="relative inline-block bg-clip-text text-transparent"
              style={{
                backgroundImage: 'linear-gradient(135deg, #dc2626 0%, #f97316 50%, #eab308 100%)',
                backgroundSize: '200% 200%',
                animation: 'text-shimmer 4s ease infinite'
              }}
            >
              Pelajar Indonesia Mendunia
              {/* Animated underline */}
              <span 
                className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                style={{
                  background: 'linear-gradient(90deg, #dc2626, #f97316, #eab308, #dc2626)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 2s linear infinite',
                }}
              />
            </span>
          </h1>
        </motion.div>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed"
        >
          {heroData.subheadline}
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <motion.a
            href="mailto:dirpusdadig@ppi.id"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="group px-8 py-4 rounded-full bg-red-600 text-white font-semibold text-base flex items-center gap-2 transition-all duration-300 hover:bg-red-700 hover:shadow-xl hover:shadow-red-600/25"
          >
            {heroData.primaryCTA}
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </motion.a>
          <motion.a
            href="mailto:dirpusdadig@ppi.id"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-4 rounded-full border-2 border-gray-300 text-gray-700 font-semibold text-base transition-all duration-300 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
          >
            {heroData.secondaryCTA}
          </motion.a>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16 grid grid-cols-2 gap-8 max-w-md mx-auto"
        >
          {[
            { value: '68+', label: 'PPI Negara' },
            { value: '6', label: 'Layanan Digital' },
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <p className="text-3xl sm:text-4xl font-bold text-gray-900 mb-1">{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 rounded-full border-2 border-gray-300 flex items-start justify-center p-2"
        >
          <motion.div
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1.5 h-1.5 rounded-full bg-gray-400"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
